import { FC, useCallback, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { formatNumber } from '@agro1desenvolvimento/utils-js';
import { sumBy } from 'lodash';
import {
  AdubacaoRow,
  RecomendacaoSemeaduraResumoAdubacao,
} from '../../../../@types';
import {
  NUMBER_FORMAT_OPTIONS,
  doseTemplate,
  getConversionToGramas,
} from '../utils';
import { RecomendacaoSemeaduraService } from '../../../../services';
import { getTotalLabel } from './util';
import defaultColumnProps from '../../../../utils/default-column-props';

const formatRegulagem = (value: number) =>
  formatNumber(value, {
    maximumFractionDigits: 4,
    useGrouping: true,
  });

const AdubosList: FC<PropTypes> = ({
  formValues,
  striped = true,
  hideOrderIcon = false,
}) => {
  const {
    adubos,
    sementeCobertura,
    comMargemSeguranca,
    distribuicaoRegulagemAdubo,
    porcentagemAreaRemonte,
    espacamento,
  } = formValues;

  const adubosList = useMemo(
    () =>
      adubos.map((adubo) => {
        const regulagem = RecomendacaoSemeaduraService.getRegulagem({
          dose: adubo.dose || 0,
          distribuicaoRegulagem: distribuicaoRegulagemAdubo || 0,
          espacamento: espacamento || 0,
        });

        const areaTotal = sumBy(adubos, 'area');

        const quantidadeRegulagem =
          RecomendacaoSemeaduraService.getQuantidadeRegulagemAdubo({
            dose: adubo.dose || 0,
            areaTotal,
            espacamento: espacamento || 0,
            distribuicaoRegulagemAdubo: distribuicaoRegulagemAdubo || 0,
          });

        const total = RecomendacaoSemeaduraService.getTotalAdubo({
          dose: adubo.dose || 0,
          porcentagemAreaRemonte: porcentagemAreaRemonte || 0,
          area: adubo.area || 0,
        });

        return { ...adubo, quantidadeRegulagem, total, regulagem };
      }),
    [adubos, distribuicaoRegulagemAdubo, espacamento, porcentagemAreaRemonte],
  );

  const regulagemAduboTemplate = useCallback(
    (rowData: RecomendacaoSemeaduraResumoAdubacao) => {
      const { regulagem, unidade } = rowData;
      const unidadeTaxa = getConversionToGramas(unidade!);

      if (comMargemSeguranca) {
        const regulagemMinima = formatRegulagem(regulagem.min * unidadeTaxa);
        const regulagemMaxima = formatRegulagem(regulagem.max * unidadeTaxa);

        return `${regulagemMinima} a ${regulagemMaxima} g`;
      }

      const regulagemResultado = formatRegulagem(
        regulagem.result * unidadeTaxa,
      );

      return `${regulagemResultado} g`;
    },
    [comMargemSeguranca],
  );

  if (adubosList.length === 0) return null;

  return (
    <div>
      <h2 className="pl-2">Adubos</h2>
      <DataTable
        sortIcon={hideOrderIcon ? () => null : undefined}
        dataKey="identification"
        value={adubosList}
        className="p-0"
        emptyMessage="Nenhuma adubo encontrada."
        stripedRows={striped}
      >
        <Column
          {...defaultColumnProps('left')}
          field="talhao.talhao.descricao"
          header="Talhão"
        />
        <Column
          {...defaultColumnProps()}
          field="area"
          header="Área (ha)"
          body={(adubo) => formatNumber(adubo.area, NUMBER_FORMAT_OPTIONS)}
        />
        <Column
          {...defaultColumnProps('left')}
          field="adubo.descricao"
          header="Adubo"
        />
        {!sementeCobertura && (
          <Column
            {...defaultColumnProps()}
            header={`Regulagem (${distribuicaoRegulagemAdubo || 10}m)`}
            headerClassName="text-danger"
            className="col regulagem"
            body={regulagemAduboTemplate}
            field="regulagem"
          />
        )}
        <Column
          {...defaultColumnProps()}
          field="dose"
          header="Dose"
          body={doseTemplate}
        />
        <Column
          {...defaultColumnProps()}
          header="Total"
          field="total"
          body={(value) => getTotalLabel(value.total, value.unidade)}
        />
      </DataTable>
    </div>
  );
};

type PropTypes = {
  formValues: {
    adubos: AdubacaoRow[];
    sementeCobertura: boolean;
    comMargemSeguranca: boolean;
    distribuicaoRegulagemAdubo?: number;
    porcentagemAreaRemonte?: number;
    espacamento?: number;
  };
  striped?: boolean;
  hideOrderIcon?: boolean;
};

export default AdubosList;
